/**
 * Single assignment row in Tab "Bài tập".
 * Shows title, type badge, submission count, status badge, and publish/close action.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import type { DashboardAssignment } from '@client/lib/queries/use-session-dashboard';
import type { AssignmentStatus } from '@client/lib/queries/use-assignment-status';

const TYPE_LABELS: Record<string, string> = {
  homework: 'Bài tập',
  quiz:     'Kiểm tra',
  project:  'Dự án',
};

const STATUS_META: Record<
  AssignmentStatus,
  { label: string; variant: 'secondary' | 'success' | 'outline' }
> = {
  draft:     { label: 'Nháp',        variant: 'secondary' },
  published: { label: 'Đã xuất bản', variant: 'success' },
  closed:    { label: 'Đã đóng',     variant: 'outline' },
};

interface AssignmentRowProps {
  assignment: DashboardAssignment;
  onStatusChange: (id: string, status: AssignmentStatus) => void;
  isPending?: boolean;
}

export function AssignmentRow({ assignment, onStatusChange, isPending }: AssignmentRowProps) {
  const statusMeta = STATUS_META[assignment.status] ?? STATUS_META.draft;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
      {/* Info */}
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium text-foreground truncate">{assignment.title}</p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="secondary" className="text-xs">
            {TYPE_LABELS[assignment.type] ?? assignment.type}
          </Badge>
          <Badge variant={statusMeta.variant} className="text-xs">
            {statusMeta.label}
          </Badge>
          {assignment.maxScore != null && <span>/ {assignment.maxScore} điểm</span>}
          {assignment.dueDate && (
            <span>
              Hạn: {new Date(assignment.dueDate).toLocaleDateString('vi-VN')}
            </span>
          )}
          <span>
            Đã nộp: {assignment.submissionCount}
            {assignment.gradedCount > 0 && ` · Chấm: ${assignment.gradedCount}`}
          </span>
        </div>
      </div>

      {/* Action button */}
      <div className="shrink-0">
        {assignment.status === 'draft' && (
          <Button
            size="sm"
            className="min-h-[36px] w-full sm:w-auto"
            disabled={isPending}
            onClick={() => onStatusChange(assignment.id, 'published')}
          >
            {isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Xuất bản
          </Button>
        )}
        {assignment.status === 'published' && (
          <Button
            size="sm"
            variant="outline"
            className="min-h-[36px] w-full sm:w-auto"
            disabled={isPending}
            onClick={() => onStatusChange(assignment.id, 'closed')}
          >
            {isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Đóng nộp
          </Button>
        )}
      </div>
    </div>
  );
}
